const DB_NAME = 'bizcore-offline';
const DB_VERSION = 3;
const SALE_STORE = 'pending-sales';
const PRODUCT_STORE = 'products-cache';
const META_STORE = 'cache-meta';

export interface PendingSale {
  id: string;
  payload: Record<string, unknown>;
  queuedAt: string;
  status: 'pending' | 'syncing' | 'failed';
  paymentMethod: string;
  total: number;
  itemCount: number;
  errorMessage?: string;
}

export interface SaleQueueMetadata {
  paymentMethod: string;
  total: number;
  itemCount: number;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = request.result;
      const oldVersion = event.oldVersion;

      if (oldVersion < 1) {
        if (!db.objectStoreNames.contains(SALE_STORE)) {
          db.createObjectStore(SALE_STORE, { keyPath: 'id' });
        }
      }

      if (oldVersion < 2) {
        if (!db.objectStoreNames.contains(PRODUCT_STORE)) {
          db.createObjectStore(PRODUCT_STORE, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(META_STORE)) {
          db.createObjectStore(META_STORE, { keyPath: 'key' });
        }
      }

      if (oldVersion < 3) {
        // Existing records in SALE_STORE have no version field.
        // All new writes will include paymentMethod, total, itemCount, errorMessage.
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function queueSale(
  payload: Record<string, unknown>,
  metadata?: SaleQueueMetadata,
): Promise<string> {
  const id = `offline-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const db = await openDB();
  const tx = db.transaction(SALE_STORE, 'readwrite');
  tx.objectStore(SALE_STORE).put({
    id,
    payload,
    queuedAt: new Date().toISOString(),
    status: 'pending',
    paymentMethod: metadata?.paymentMethod ?? 'Cash',
    total: metadata?.total ?? 0,
    itemCount: metadata?.itemCount ?? 0,
    errorMessage: undefined,
  });
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });

  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    const reg = await navigator.serviceWorker.ready;
    await (reg as ServiceWorkerRegistration & { sync: { register(tag: string): Promise<void> } }).sync.register('sync-offline-queue');
  }

  return id;
}

export async function getPendingSales(): Promise<PendingSale[]> {
  const db = await openDB();
  const tx = db.transaction(SALE_STORE, 'readonly');
  const store = tx.objectStore(SALE_STORE);
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result as PendingSale[]);
    request.onerror = () => reject(request.error);
  });
}

export async function removePendingSale(id: string): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(SALE_STORE, 'readwrite');
  tx.objectStore(SALE_STORE).delete(id);
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function updateSaleStatus(
  id: string,
  status: PendingSale['status'],
  errorMessage?: string,
): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(SALE_STORE, 'readwrite');
  const store = tx.objectStore(SALE_STORE);
  const request = store.get(id);
  request.onsuccess = () => {
    const record = request.result as PendingSale | undefined;
    if (record) {
      record.status = status;
      if (errorMessage !== undefined) record.errorMessage = errorMessage;
      store.put(record);
    }
  };
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getPendingSaleStats(): Promise<{
  pending: number;
  failed: number;
  totalAmount: number;
}> {
  const sales = await getPendingSales();
  return sales.reduce(
    (acc, sale) => {
      if (sale.status === 'pending') acc.pending++;
      if (sale.status === 'failed') acc.failed++;
      acc.totalAmount += sale.total ?? 0;
      return acc;
    },
    { pending: 0, failed: 0, totalAmount: 0 },
  );
}

export async function syncPendingSales(): Promise<{ synced: number; failed: number }> {
  const sales = await getPendingSales();
  let synced = 0;
  let failed = 0;

  // Dynamic import to avoid circular dependency
  const { default: axiosInstance } = await import('../api/axiosInstance');

  for (const sale of sales) {
    if (sale.status === 'syncing') continue;

    await updateSaleStatus(sale.id, 'syncing');

    try {
      const response = await axiosInstance.post('/pos/transactions', sale.payload);
      if (response.data?.success) {
        await removePendingSale(sale.id);
        synced++;
      } else {
        await updateSaleStatus(sale.id, 'failed', response.data?.message || 'Sync failed');
        failed++;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Network error';
      await updateSaleStatus(sale.id, 'failed', message);
      failed++;
      // If we're still offline, stop trying more sales
      if (!navigator.onLine) break;
    }
  }

  return { synced, failed };
}

/* ─── Product Cache ─────────────────────────────────────────── */

/**
 * Replace the full product cache with a fresh product list.
 * Products are stored individually (one object store entry per product)
 * so they can be queried individually if ever needed.
 */
export async function cacheProducts(products: Array<Record<string, unknown>>): Promise<void> {
  const db = await openDB();
  const tx = db.transaction([PRODUCT_STORE, META_STORE], 'readwrite');

  // Clear existing products, then bulk-insert
  const productStore = tx.objectStore(PRODUCT_STORE);
  productStore.clear();
  for (const product of products) {
    productStore.put(product);
  }

  // Update metadata
  const metaStore = tx.objectStore(META_STORE);
  metaStore.put({
    key: 'products',
    count: products.length,
    cachedAt: new Date().toISOString(),
  });

  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Read all products from the local cache. */
export async function getCachedProducts(): Promise<Array<Record<string, unknown>>> {
  const db = await openDB();
  const tx = db.transaction(PRODUCT_STORE, 'readonly');
  const store = tx.objectStore(PRODUCT_STORE);
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Remove all cached products (e.g. after a product create/update). */
export async function clearProductCache(): Promise<void> {
  const db = await openDB();
  const tx = db.transaction([PRODUCT_STORE, META_STORE], 'readwrite');
  tx.objectStore(PRODUCT_STORE).clear();
  tx.objectStore(META_STORE).delete('products');
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Get metadata about the cached product list. Returns null if not cached. */
export async function getProductCacheMeta(): Promise<{ count: number; cachedAt: string } | null> {
  const db = await openDB();
  const tx = db.transaction(META_STORE, 'readonly');
  const store = tx.objectStore(META_STORE);
  return new Promise((resolve, reject) => {
    const request = store.get('products');
    request.onsuccess = () => {
      const result = request.result as { key: string; count: number; cachedAt: string } | undefined;
      resolve(result ? { count: result.count, cachedAt: result.cachedAt } : null);
    };
    request.onerror = () => reject(request.error);
  });
}
